import { useEffect, useRef, useState } from 'react';
import { Modal } from './Modal.jsx';
import { Icon } from './Icon.jsx';
import { useT } from '../i18n/index.js';
import { useAccount } from '../store/accountStore.js';

const SIG_WIDTH = 600;
const SIG_HEIGHT = 220;

export function SignatureModal({ open, onClose, onSave }) {
  const t = useT();
  const savedSignatures = useAccount((s) => s.signatures);
  const TABS = savedSignatures.length
    ? ['saved', 'draw', 'type', 'upload']
    : ['draw', 'type', 'upload'];
  const [tab, setTab] = useState('draw');
  const [typed, setTyped] = useState('');
  const [uploaded, setUploaded] = useState(null);
  const [hasInk, setHasInk] = useState(false);
  const canvasRef = useRef(null);
  const drawState = useRef({ drawing: false, last: null });

  useEffect(() => {
    if (!open) return;
    setTab(savedSignatures.length ? 'saved' : 'draw');
    setTyped('');
    setUploaded(null);
    setHasInk(false);
    requestAnimationFrame(() => clearCanvas());
  }, [open]);

  const ctxOf = () => {
    const c = canvasRef.current;
    if (!c) return null;
    const ctx = c.getContext('2d');
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0b0d12';
    ctx.lineWidth = 2.2;
    return ctx;
  };

  const clearCanvas = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, c.width, c.height);
    setHasInk(false);
  };

  const point = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return {
      x: ((t.clientX - rect.left) / rect.width) * c.width,
      y: ((t.clientY - rect.top) / rect.height) * c.height,
    };
  };

  const startDraw = (e) => {
    e.preventDefault();
    drawState.current.drawing = true;
    drawState.current.last = point(e);
  };

  const moveDraw = (e) => {
    if (!drawState.current.drawing) return;
    e.preventDefault();
    const ctx = ctxOf();
    if (!ctx) return;
    const p = point(e);
    ctx.beginPath();
    ctx.moveTo(drawState.current.last.x, drawState.current.last.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    drawState.current.last = p;
    if (!hasInk) setHasInk(true);
  };

  const endDraw = () => {
    drawState.current.drawing = false;
  };

  const buildFromCanvas = () => {
    const c = canvasRef.current;
    if (!c || !hasInk) return null;
    const trimmed = trimTransparent(c);
    return {
      dataUrl: trimmed.dataUrl,
      w: trimmed.width,
      h: trimmed.height,
    };
  };

  const buildFromTyped = () => {
    if (!typed.trim()) return null;
    const c = document.createElement('canvas');
    c.width = SIG_WIDTH;
    c.height = SIG_HEIGHT;
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.fillStyle = '#0b0d12';
    ctx.font = `italic 700 110px "Dancing Script", "Brush Script MT", "Apple Chancery", cursive`;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';
    ctx.fillText(typed.trim(), c.width / 2, c.height / 2);
    const trimmed = trimTransparent(c);
    return {
      dataUrl: trimmed.dataUrl,
      w: trimmed.width,
      h: trimmed.height,
    };
  };

  const buildFromUpload = () => {
    if (!uploaded) return null;
    return uploaded;
  };

  const handleUpload = async (file) => {
    if (!file) return;
    const dataUrl = await new Promise((res) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.readAsDataURL(file);
    });
    const img = await new Promise((res) => {
      const i = new Image();
      i.onload = () => res(i);
      i.src = dataUrl;
    });
    setUploaded({ dataUrl, w: img.width, h: img.height });
  };

  const [pickedSaved, setPickedSaved] = useState(null);

  const save = () => {
    let out = null;
    if (tab === 'saved') {
      const s = savedSignatures.find((x) => x.id === pickedSaved);
      out = s ? { dataUrl: s.dataUrl, w: s.w, h: s.h } : null;
    } else if (tab === 'draw') {
      out = buildFromCanvas();
    } else if (tab === 'type') {
      out = buildFromTyped();
    } else {
      out = buildFromUpload();
    }
    if (!out) return;
    onSave(out);
  };

  const canSave =
    (tab === 'saved' && !!pickedSaved) ||
    (tab === 'draw' && hasInk) ||
    (tab === 'type' && typed.trim().length > 0) ||
    (tab === 'upload' && uploaded);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('signature.title')}
      size="lg"
      footer={
        <>
          <button className="btn sm" onClick={onClose}>{t('modal.cancel')}</button>
          {tab === 'draw' && (
            <button className="btn sm ghost" onClick={clearCanvas}>
              <Icon name="trash" size={14} /> {t('signature.clear')}
            </button>
          )}
          <span className="grow" />
          <button className="btn primary sm" onClick={save} disabled={!canSave}>
            <Icon name="check" size={14} /> {t('signature.save')}
          </button>
        </>
      }
    >
      <div className="sig-tabs">
        {TABS.map((k) => (
          <button
            key={k}
            className={`sig-tab ${tab === k ? 'active' : ''}`}
            onClick={() => setTab(k)}
          >
            {t(`signature.tab_${k}`)}
          </button>
        ))}
      </div>

      <div className="sig-body">
        {tab === 'saved' && (
          <>
            <div className="tiny muted" style={{ marginBottom: 8 }}>{t('signature.saved_hint')}</div>
            <div className="sig-saved-grid">
              {savedSignatures.map((s) => (
                <button
                  key={s.id}
                  className={`sig-saved-cell ${pickedSaved === s.id ? 'on' : ''}`}
                  onClick={() => setPickedSaved(s.id)}
                >
                  <img src={s.dataUrl} alt={s.label} />
                  <span className="tiny">{s.label}</span>
                </button>
              ))}
            </div>
          </>
        )}
        {tab === 'draw' && (
          <>
            <div className="tiny muted" style={{ marginBottom: 8 }}>{t('signature.draw_hint')}</div>
            <div className="sig-canvas-wrap">
              <canvas
                ref={canvasRef}
                width={SIG_WIDTH}
                height={SIG_HEIGHT}
                onMouseDown={startDraw}
                onMouseMove={moveDraw}
                onMouseUp={endDraw}
                onMouseLeave={endDraw}
                onTouchStart={startDraw}
                onTouchMove={moveDraw}
                onTouchEnd={endDraw}
              />
            </div>
          </>
        )}

        {tab === 'type' && (
          <>
            <div className="tiny muted" style={{ marginBottom: 8 }}>{t('signature.type_hint')}</div>
            <input
              className="sig-type-input"
              placeholder={t('signature.type_placeholder')}
              aria-label={t('signature.type_placeholder')}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoFocus
            />
            <div className="sig-preview">
              <span>{typed || t('signature.type_placeholder')}</span>
            </div>
          </>
        )}

        {tab === 'upload' && (
          <>
            <div className="tiny muted" style={{ marginBottom: 8 }}>{t('signature.upload_hint')}</div>
            <label className="sig-upload">
              <input
                type="file"
                accept="image/png,image/jpeg"
                className="sr-only"
                aria-label={t('signature.tab_upload')}
                onChange={(e) => handleUpload(e.target.files?.[0])}
              />
              {uploaded ? (
                <img src={uploaded.dataUrl} alt="signature" />
              ) : (
                <div className="sig-upload-empty">
                  <Icon name="upload" size={24} />
                  <span>{t('signature.tab_upload')}</span>
                </div>
              )}
            </label>
          </>
        )}

        <div className="sig-secure">
          <Icon name="check" size={14} /> {t('signature.secure_note')}
        </div>
      </div>
    </Modal>
  );
}

function trimTransparent(canvas) {
  const ctx = canvas.getContext('2d');
  const { width, height } = canvas;
  const data = ctx.getImageData(0, 0, width, height).data;
  let top = height, bottom = 0, left = width, right = 0;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const a = data[(y * width + x) * 4 + 3];
      if (a > 0) {
        if (y < top) top = y;
        if (y > bottom) bottom = y;
        if (x < left) left = x;
        if (x > right) right = x;
      }
    }
  }
  if (right < left || bottom < top) {
    return { dataUrl: canvas.toDataURL('image/png'), width, height };
  }
  const pad = 6;
  top = Math.max(0, top - pad);
  left = Math.max(0, left - pad);
  bottom = Math.min(height - 1, bottom + pad);
  right = Math.min(width - 1, right + pad);
  const w = right - left + 1;
  const h = bottom - top + 1;
  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  out.getContext('2d').drawImage(canvas, left, top, w, h, 0, 0, w, h);
  return { dataUrl: out.toDataURL('image/png'), width: w, height: h };
}
